/**
 * Client for the Champions Hub public courses API.
 * Docs: https://champions-hub.com/api/v1/courses
 */

const API_BASE = 'https://champions-hub.com/api/v1';

/**
 * Initiative-scoped partner registry. Static courses use these consistent
 * partner identifiers so the picker can render real logos / brand names.
 */
type InitiativePartnerKey = 'barca' | 'ekkono' | 'champions-hub' | 'ac-football-center';

const INITIATIVE_PARTNERS: Record<
  InitiativePartnerKey,
  { name: BilingualText; slug: string; logo: string }
> = {
  barca: {
    name: { en: 'Barça Innovation Hub', ar: 'Barça Innovation Hub' },
    slug: 'barca-innovation-hub',
    logo: '',
  },
  ekkono: {
    name: { en: 'Ekkono Coaches Academy', ar: 'أكاديمية إيكونو للمدرّبين' },
    slug: 'ekkono',
    logo: '/logos/ekkono.png',
  },
  'champions-hub': {
    name: { en: 'Champions Hub', ar: 'Champions Hub' },
    slug: 'champions-hub',
    logo: '/logos/champions-hub-horizontal-light.png',
  },
  'ac-football-center': {
    name: { en: 'AC Football Center', ar: 'AC Football Center' },
    slug: 'ac-football-center',
    logo: '/logos/ac-football-center.png',
  },
};

/**
 * 25 seeded initiative courses. The apply-form picker reads ONLY this list —
 * no Champions Hub API call is made for the picker. Champions Hub remains
 * source of truth for non-initiative pages; this data set is the canonical
 * initiative curriculum and can be backfilled to the DB when ready.
 */
interface StaticCourseSeed {
  slug: string;
  partner: InitiativePartnerKey;
  title: BilingualText;
  category: BilingualText;
  duration: BilingualText;
  targetAudience: BilingualText;
}

const COURSE_SEEDS: StaticCourseSeed[] = [
  // ───────── Barça (5) ─────────
  {
    slug: 'barca-coach-academy-introductory-level',
    partner: 'barca',
    title: {
      ar: 'أكاديمية مدربي Barça: المستوى التمهيدي',
      en: 'Barça Coach Academy: Introductory Level',
    },
    category: { ar: 'التدريب في كرة القدم', en: 'Football coaching' },
    duration: { ar: '3 شهور', en: '3 months' },
    targetAudience: {
      ar: 'مدربو الأكاديميات والتطوير الرياضي',
      en: 'Academy coaches & sports development staff',
    },
  },
  {
    slug: 'certificate-in-psychology-for-high-performance-sports',
    partner: 'barca',
    title: {
      ar: 'شهادة في علم النفس للرياضات عالية الأداء',
      en: 'Certificate in Psychology for High Performance Sports',
    },
    category: { ar: 'علم النفس الرياضي', en: 'Sports psychology' },
    duration: { ar: '4 شهور', en: '4 months' },
    targetAudience: {
      ar: 'العاملون في علم النفس والأداء الرياضي',
      en: 'Sports psychology & performance practitioners',
    },
  },
  {
    slug: 'certificate-in-sports-marketing-and-sponsorship',
    partner: 'barca',
    title: {
      ar: 'شهادة في التسويق الرياضي والرعاية',
      en: 'Certificate in Sports Marketing and Sponsorship',
    },
    category: { ar: 'التسويق والرعاية الرياضية', en: 'Sports marketing & sponsorship' },
    duration: { ar: '4 شهور', en: '4 months' },
    targetAudience: {
      ar: 'مهتمو التسويق والاستثمار الرياضي',
      en: 'Sports marketing & investment professionals',
    },
  },
  {
    slug: 'certificate-in-training-for-team-sports',
    partner: 'barca',
    title: {
      ar: 'شهادة في التدريب للرياضات الجماعية',
      en: 'Certificate in Training for Team Sports',
    },
    category: { ar: 'الإعداد البدني واللياقة', en: 'Strength & conditioning' },
    duration: { ar: '4 شهور', en: '4 months' },
    targetAudience: {
      ar: 'مدربو ومختصو الألعاب الجماعية',
      en: 'Team-sport coaches & specialists',
    },
  },
  {
    slug: 'certificate-in-neurobiology-and-sports-performance',
    partner: 'barca',
    title: {
      ar: 'شهادة في البيولوجيا العصبية والأداء الرياضي',
      en: 'Certificate in Neurobiology and Sports Performance',
    },
    category: { ar: 'علم النفس الرياضي', en: 'Sports psychology' },
    duration: { ar: '4 شهور', en: '4 months' },
    targetAudience: {
      ar: 'مختصو علوم الحركة والأداء الرياضي',
      en: 'Movement science & performance specialists',
    },
  },
  // ───────── Ekkono (5) ─────────
  {
    slug: 'ekkono-method-pillars',
    partner: 'ekkono',
    title: { ar: 'ركائز منهج إيكونو', en: 'The Ekkono Method Pillars' },
    category: {
      ar: 'التدريب في كرة القدم والتحليل التكتيكي',
      en: 'Football coaching & tactical analysis',
    },
    duration: { ar: 'أسبوعان', en: '2 weeks' },
    targetAudience: {
      ar: 'مدربو ومديرو الأكاديميات الرياضية',
      en: 'Academy coaches & directors',
    },
  },
  {
    slug: 'introduction-to-game-model',
    partner: 'ekkono',
    title: { ar: 'مقدمة في نموذج اللعب', en: 'Introduction to Game Model' },
    category: {
      ar: 'التدريب في كرة القدم والتحليل التكتيكي',
      en: 'Football coaching & tactical analysis',
    },
    duration: { ar: 'أسبوعان', en: '2 weeks' },
    targetAudience: {
      ar: 'المدربون والمحللون التكتيكيون',
      en: 'Coaches & tactical analysts',
    },
  },
  {
    slug: 'introduction-to-individual-fundamentals-by-position',
    partner: 'ekkono',
    title: {
      ar: 'مقدمة في الأساسيات الفردية حسب المركز',
      en: 'Introduction to Individual Fundamentals by Position',
    },
    category: {
      ar: 'التدريب في كرة القدم والتحليل التكتيكي',
      en: 'Football coaching & tactical analysis',
    },
    duration: { ar: '3 شهور', en: '3 months' },
    targetAudience: {
      ar: 'المدربون ومحللو الأداء والكشافة',
      en: 'Coaches, performance analysts & scouts',
    },
  },
  {
    slug: 'introduction-to-team-fundamentals',
    partner: 'ekkono',
    title: { ar: 'مقدمة في أساسيات عمل الفريق', en: 'Introduction to Team Fundamentals' },
    category: {
      ar: 'التدريب في كرة القدم والتحليل التكتيكي',
      en: 'Football coaching & tactical analysis',
    },
    duration: { ar: 'شهر واحد', en: '1 month' },
    targetAudience: {
      ar: 'العاملون في التدريب والعمل الجماعي',
      en: 'Coaches working on team play',
    },
  },
  {
    slug: 'ekkono-advanced-training-methodology',
    partner: 'ekkono',
    title: { ar: 'منهجية تدريب متقدمة', en: 'Advanced Training Methodology' },
    category: {
      ar: 'التدريب في كرة القدم والتحليل التكتيكي',
      en: 'Football coaching & tactical analysis',
    },
    duration: { ar: 'شهر واحد', en: '1 month' },
    targetAudience: {
      ar: 'مدربو التطوير والأداء البدني',
      en: 'Development & physical-performance coaches',
    },
  },
  // ───────── Champions HUB (14) ─────────
  {
    slug: 'hub-high-performance-leadership',
    partner: 'champions-hub',
    title: { ar: 'القيادة عالية الأداء', en: 'High Performance Leadership' },
    category: { ar: 'علم النفس الرياضي', en: 'Sports psychology' },
    duration: { ar: 'شهر واحد', en: '1 month' },
    targetAudience: {
      ar: 'قادة الفرق والإدارة الرياضية',
      en: 'Team leaders & sports management',
    },
  },
  {
    slug: 'hub-psychological-keys-training-competition',
    partner: 'champions-hub',
    title: {
      ar: 'المفاتيح النفسية في التدريب والمنافسة في الرياضات عالية الأداء',
      en: 'Psychological Keys in Training and Competition for High Performance Sports',
    },
    category: { ar: 'علم النفس الرياضي', en: 'Sports psychology' },
    duration: { ar: 'شهر واحد', en: '1 month' },
    targetAudience: {
      ar: 'مدربو ومختصو الإعداد النفسي',
      en: 'Coaches & mental-prep specialists',
    },
  },
  {
    slug: 'hub-mental-strength-athletes',
    partner: 'champions-hub',
    title: { ar: 'القوة الذهنية لدى الرياضيين', en: 'Mental Strength in Athletes' },
    category: { ar: 'علم النفس الرياضي', en: 'Sports psychology' },
    duration: { ar: 'شهر واحد', en: '1 month' },
    targetAudience: {
      ar: 'مختصو الدعم الذهني والرياضيون',
      en: 'Mental-support specialists & athletes',
    },
  },
  {
    slug: 'hub-elite-athletic-performance-3-0',
    partner: 'champions-hub',
    title: {
      ar: 'الأداء الرياضي المتميز في بيئات الجيل الثالث',
      en: 'Elite Athletic Performance in 3.0 Environments',
    },
    category: { ar: 'علم النفس الرياضي', en: 'Sports psychology' },
    duration: { ar: 'شهر واحد', en: '1 month' },
    targetAudience: {
      ar: 'مهتمو التكنولوجيا والأداء الرياضي',
      en: 'Tech-curious sports-performance staff',
    },
  },
  {
    slug: 'hub-life-of-an-elite-athlete',
    partner: 'champions-hub',
    title: { ar: 'حياة الرياضي المتميز', en: 'The Life of an Elite Athlete' },
    category: { ar: 'علم النفس الرياضي', en: 'Sports psychology' },
    duration: { ar: 'شهر واحد', en: '1 month' },
    targetAudience: {
      ar: 'مختصو تطوير ورعاية الرياضيين',
      en: 'Athlete development & welfare specialists',
    },
  },
  {
    slug: 'hub-psychological-intervention-techniques',
    partner: 'champions-hub',
    title: { ar: 'تقنيات التدخل النفسي', en: 'Psychological Intervention Techniques' },
    category: { ar: 'علم النفس الرياضي', en: 'Sports psychology' },
    duration: { ar: 'شهر واحد', en: '1 month' },
    targetAudience: {
      ar: 'مختصو التدخل والدعم النفسي الرياضي',
      en: 'Sports-psychology intervention specialists',
    },
  },
  {
    slug: 'hub-psychological-variables-sports-performance',
    partner: 'champions-hub',
    title: {
      ar: 'المتغيرات النفسية والأداء الرياضي',
      en: 'Psychological Variables and Sports Performance',
    },
    category: { ar: 'علم النفس الرياضي', en: 'Sports psychology' },
    duration: { ar: 'شهر واحد', en: '1 month' },
    targetAudience: {
      ar: 'مختصو الأداء النفسي والتركيز الرياضي',
      en: 'Mental-performance & focus specialists',
    },
  },
  {
    slug: 'hub-contextualizing-elite-athlete-environment',
    partner: 'champions-hub',
    title: {
      ar: 'سياق بيئة الرياضي المتميز',
      en: 'Contextualizing the Elite Athlete Environment',
    },
    category: { ar: 'علم النفس الرياضي', en: 'Sports psychology' },
    duration: { ar: 'شهر واحد', en: '1 month' },
    targetAudience: {
      ar: 'المدربون الرياضيون في الألعاب الفردية والجماعية، الأخصائيون النفسيون الرياضيون، العاملون في تطوير الناشئين والتكوين الرياضي، مسؤولو الأكاديميات الرياضية ومراكز إعداد المواهب، معلمو التربية البدنية، العاملون في الأداء العالي والاحتراف الرياضي، وأولياء أمور الرياضيين الناشئين.',
      en: 'Coaches across individual and team sports, sports psychologists, youth-development staff, academy and talent-center leadership, physical-education teachers, high-performance staff, and parents of young athletes invested in their educational and psychological role.',
    },
  },
  {
    slug: 'hub-icdl',
    partner: 'champions-hub',
    title: {
      ar: 'الرخصة الدولية لقيادة الكمبيوتر (ICDL)',
      en: 'ICDL — International Computer Driving Licence',
    },
    category: { ar: 'تكنولوجيا', en: 'Tech' },
    duration: { ar: 'شهر واحد', en: '1 month' },
    targetAudience: {
      ar: 'الطلبة والباحثون عن مهارات رقمية',
      en: 'Students & professionals seeking digital skills',
    },
  },
  {
    slug: 'hub-ai-1',
    partner: 'champions-hub',
    title: { ar: 'الذكاء الاصطناعي — مبدئي', en: 'AI 1 — Introduction to Artificial Intelligence' },
    category: { ar: 'تكنولوجيا', en: 'Tech' },
    duration: { ar: 'شهر واحد', en: '1 month' },
    targetAudience: {
      ar: 'المبتدئون في الذكاء الاصطناعي',
      en: 'Beginners in artificial intelligence',
    },
  },
  {
    slug: 'hub-ai-2',
    partner: 'champions-hub',
    title: { ar: 'الذكاء الاصطناعي — متقدم', en: 'AI 2 — Advanced Artificial Intelligence' },
    category: { ar: 'تكنولوجيا', en: 'Tech' },
    duration: { ar: 'أسبوعان', en: '2 weeks' },
    targetAudience: {
      ar: 'المهتمون بالتطبيقات المتقدمة للذكاء الاصطناعي',
      en: 'Practitioners in advanced AI applications',
    },
  },
  {
    slug: 'hub-football-metabolism-nutrition',
    partner: 'champions-hub',
    title: {
      ar: 'مقدمة في أيض كرة القدم والتغذية الرياضية',
      en: 'An Introduction to Football Metabolism and Nutrition',
    },
    category: { ar: 'التغذية', en: 'Nutrition' },
    duration: { ar: 'شهر واحد', en: '1 month' },
    targetAudience: {
      ar: 'مهتمو التغذية الرياضية وأداء الرياضيين',
      en: 'Sports-nutrition & performance enthusiasts',
    },
  },
  {
    slug: 'hub-advanced-sports-nutrition',
    partner: 'champions-hub',
    title: { ar: 'التغذية الرياضية المتقدمة', en: 'Advanced Sports Nutrition' },
    category: { ar: 'التغذية', en: 'Nutrition' },
    duration: { ar: 'شهر واحد', en: '1 month' },
    targetAudience: {
      ar: 'مختصو التغذية الرياضية والمحترفون',
      en: 'Sports-nutrition specialists & professionals',
    },
  },
  {
    slug: 'hub-body-composition-somatotype-performance',
    partner: 'champions-hub',
    title: {
      ar: 'تركيب الجسم، والنمط الجسمي، والأداء الرياضي',
      en: 'Body Composition, Somatotype and Athletic Performance',
    },
    category: { ar: 'التغذية', en: 'Nutrition' },
    duration: { ar: 'شهر واحد', en: '1 month' },
    targetAudience: {
      ar: 'مختصو الأداء البدني وعلوم الرياضة',
      en: 'Performance & sports-science specialists',
    },
  },
  // ───────── AC Football Center (1) ─────────
  {
    slug: 'youth-academy-coordination',
    partner: 'ac-football-center',
    title: { ar: 'تنسيق أكاديمية الشباب', en: 'Youth Academy Coordination' },
    category: { ar: 'التدريب في كرة القدم', en: 'Football coaching' },
    duration: { ar: 'شهران', en: '2 months' },
    targetAudience: {
      ar: 'مديرو ومنسقو أكاديميات كرة القدم',
      en: 'Football academy directors & coordinators',
    },
  },
];

function buildStaticCourse(seed: StaticCourseSeed): Course {
  const p = INITIATIVE_PARTNERS[seed.partner];
  return {
    id: seed.slug,
    slug: seed.slug,
    title: seed.title,
    description: { en: '', ar: '' },
    level: 'introductory',
    duration: seed.duration.en,
    durationBilingual: seed.duration,
    targetAudienceBilingual: seed.targetAudience,
    price: 0,
    currency: 'USD',
    prices: null,
    deliveryLanguages: ['en', 'ar'],
    deliveryLanguage: 'en',
    priceVariants: {},
    rating: 0,
    studentCount: 0,
    image: '',
    partnerLogo: p.logo,
    partnerName: p.name,
    partnerSlug: p.slug,
    partnerWebsite: undefined,
    categorySlug: seed.category.en.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
    categoryName: seed.category,
    publishedAt: '',
    enrollmentOpensAt: null,
    enrollmentStatus: null,
    enrollmentState: 'open',
  };
}

export const STATIC_INITIATIVE_COURSES: Course[] = COURSE_SEEDS.map(buildStaticCourse);

/** Display order = order of COURSE_SEEDS. */
export const INITIATIVE_COURSE_SLUGS = COURSE_SEEDS.map((s) => s.slug);

const INITIATIVE_SLUG_SET = new Set<string>(INITIATIVE_COURSE_SLUGS);

const INITIATIVE_ORDER: Record<string, number> = INITIATIVE_COURSE_SLUGS.reduce(
  (acc, slug, i) => {
    acc[slug] = i;
    return acc;
  },
  {} as Record<string, number>
);

export function isStaticInitiativeCourse(c: Pick<Course, 'slug'>): boolean {
  return INITIATIVE_SLUG_SET.has(c.slug);
}
/**
 * Same-origin snapshot of the courses catalogue. Generated by
 * `scripts/sync-courses.mjs` (or the equivalent shell snippet in CLAUDE.md)
 * and committed to `public/champions-hub-courses.json`. Used as a fallback
 * because the live API doesn't send CORS headers from other origins.
 */
const SNAPSHOT_PATH = '/champions-hub-courses.json';

export interface BilingualText {
  en: string;
  ar: string;
}

export interface CourseLevel {
  raw: 'beginner' | 'intermediate' | 'advanced' | 'introductory';
}

export interface PriceVariant {
  listPrice: number;
  finalPrice: number;
  currency: string;
  discountPct: number;
}

export interface Course {
  id: string;
  slug: string;
  title: BilingualText;
  description: BilingualText;
  level: CourseLevel['raw'];
  duration: string;
  /** Static initiative courses use this bilingual override (e.g. "3 شهور" / "3 months"). */
  durationBilingual?: BilingualText;
  /** Static initiative courses can carry their target audience text directly. */
  targetAudienceBilingual?: BilingualText;
  /** Stored as cents (price / 100 = USD). */
  price: number;
  currency: string;
  prices: Record<string, number> | null;
  deliveryLanguages: string[];
  deliveryLanguage: string;
  priceVariants: Record<string, PriceVariant>;
  rating: number;
  studentCount: number;
  image: string;
  partnerLogo: string;
  partnerName: BilingualText;
  partnerSlug: 'barca-innovation-hub' | 'ac-football-center' | 'ekkono' | string;
  partnerWebsite?: string;
  categorySlug: string;
  categoryName: BilingualText;
  publishedAt: string;
  enrollmentOpensAt: string | null;
  enrollmentStatus: string | null;
  enrollmentState: 'open' | 'coming_soon' | 'closed' | string;
}

export interface CoursesResponse {
  data: Course[];
  meta: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
  };
}

/**
 * Detail endpoint shape (GET /courses/{slug}?lang=…). Bilingual fields are
 * pre-localised to a single string per the `lang` parameter, so this is NOT
 * the same as `Course` from the list endpoint.
 */
export interface CourseDetail {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  description: string;
  longDescription: string | null;
  level: Course['level'];
  duration: string;
  weeklyHours: string | number | null;
  startDate: string | null;
  language: string;
  deliveryLanguages: string[];
  deliveryLanguage: string;
  deliveryMode: string | null;
  price: number;
  currency: string;
  pricesByLanguage: Record<string, number>;
  priceVariants: Record<string, PriceVariant>;
  installmentCount: number | null;
  installmentAmount: number | null;
  rating: number;
  studentCount: number;
  reviewsCount: number;
  reviewsAvgRating: number;
  image: string;
  bannerImage: string | null;
  partnerName: string;
  partnerSlug: string;
  partnerLogo: string;
  partnerWebsite: string | null;
  categoryName: string;
  categorySlug: string;
  publishedAt: string;
  enrollmentStatus: string | null;
  enrollmentState: string;
  outcomes: string[] | null;
  includes: string[] | null;
  targetAudience: string | null;
  learningExperience: string | null;
  instructorQuote: string | null;
  instructors: {
    id: string;
    name: string;
    title?: string;
    bio?: string;
    credentials?: string[];
    avatar?: string;
  }[] | null;
  modules: {
    id: string;
    title: string;
    duration: string | null;
    sortOrder: number;
    lessons: {
      id: string;
      title: string;
      duration: string | null;
      isPreview: boolean;
      sortOrder: number;
    }[];
  }[] | null;
  faqs: { id: string; question: string; answer: string; sortOrder: number }[] | null;
  reviews: { id: string; author: string; rating: number; comment: string }[] | null;
  accreditationName: string | null;
  accreditationLogo: string | null;
  accreditationUrl: string | null;
  accreditationDescription: string | null;
  accreditationPoints: string[] | null;
  developerName: string | null;
  developerLogo: string | null;
  developerUrl: string | null;
  developerDescription: string | null;
  developerPoints: string[] | null;
  courseUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FetchCoursesParams {
  lang?: 'en' | 'ar';
  currency?: 'USD' | 'EUR' | 'EGP' | 'SAR';
  category?: string;
  partner?: string;
  level?: string;
  search?: string;
  sort?: 'newest' | 'rating' | 'price_asc' | 'price_desc' | 'popular';
  page?: number;
  limit?: number;
}

export async function fetchCourses(params: FetchCoursesParams = {}): Promise<CoursesResponse> {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  });
  const url = `${API_BASE}/courses?${qs.toString()}`;
  const res = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!res.ok) {
    throw new Error(`Champions Hub API error ${res.status}: ${res.statusText}`);
  }
  return res.json();
}

async function fetchSnapshot(): Promise<Course[] | null> {
  try {
    const res = await fetch(SNAPSHOT_PATH, { headers: { Accept: 'application/json' } });
    if (!res.ok) return null;
    const json = await res.json();
    return Array.isArray(json?.data) ? (json.data as Course[]) : null;
  } catch {
    return null;
  }
}

/**
 * Fetch all courses. Tries the same-origin snapshot first (bypasses CORS),
 * falls back to direct API. Locally filters by lang/partner/level.
 *
 * If `params.initiativeOnly` is true, the result is restricted to the
 * `INITIATIVE_COURSE_SLUGS` allowlist and sorted in that exact order.
 */
export async function fetchAllCourses(
  params: FetchCoursesParams & { initiativeOnly?: boolean } = {}
): Promise<Course[]> {
  // Initiative form is fully seeded — no API call, no snapshot. Future DB
  // backfill replaces this list, not the API.
  if (params.initiativeOnly) {
    return [...STATIC_INITIATIVE_COURSES].sort(
      (a, b) =>
        (INITIATIVE_ORDER[a.slug] ?? 999) - (INITIATIVE_ORDER[b.slug] ?? 999)
    );
  }

  const filterAndOrder = (courses: Course[]): Course[] =>
    courses.filter((c) => {
      if (params.partner && c.partnerSlug !== params.partner) return false;
      if (params.level && c.level !== params.level) return false;
      return true;
    });

  const snapshot = await fetchSnapshot();
  if (snapshot) return filterAndOrder(snapshot);

  const limit = params.limit ?? 100;
  const first = await fetchCourses({ ...params, page: 1, limit });
  if (first.meta.totalPages <= 1) return filterAndOrder(first.data);

  const remaining = await Promise.all(
    Array.from({ length: first.meta.totalPages - 1 }, (_, i) =>
      fetchCourses({ ...params, page: i + 2, limit })
    )
  );
  return filterAndOrder([first, ...remaining].flatMap((r) => r.data));
}

/** Convert API "cents-like" minor unit to real USD. */
export function courseUsd(course: { price: number }): number {
  return Math.round(course.price / 100);
}

const DETAIL_SNAPSHOT_PATH = (lang: 'en' | 'ar') =>
  `/champions-hub-course-details-${lang}.json`;

const detailSnapshotCache: Record<'en' | 'ar', Promise<Record<string, CourseDetail>> | null> = {
  en: null,
  ar: null,
};

async function loadDetailSnapshot(lang: 'en' | 'ar'): Promise<Record<string, CourseDetail>> {
  if (!detailSnapshotCache[lang]) {
    detailSnapshotCache[lang] = (async () => {
      try {
        const res = await fetch(DETAIL_SNAPSHOT_PATH(lang), {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) return {};
        const json = await res.json();
        return (json?.data as Record<string, CourseDetail>) ?? {};
      } catch {
        return {};
      }
    })();
  }
  return detailSnapshotCache[lang]!;
}

export async function fetchCourseDetail(
  slug: string,
  lang: 'en' | 'ar' = 'en'
): Promise<CourseDetail | null> {
  // Seeded initiative courses are static — no detail endpoint to fetch.
  if (INITIATIVE_SLUG_SET.has(slug)) return null;

  // Try same-origin snapshot first (bypasses CORS).
  const snap = await loadDetailSnapshot(lang);
  if (snap[slug]) return snap[slug];

  // Fall back to direct API (only works if CORS allows it).
  try {
    const url = `${API_BASE}/courses/${encodeURIComponent(slug)}?lang=${lang}&currency=USD`;
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) return null;
    const json = await res.json();
    // Detail endpoint may flatten or wrap in `data`.
    return (json?.data ?? json) as CourseDetail;
  } catch {
    return null;
  }
}
